import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtErr, getToken, API, bytesToHuman } from "../lib/api";
import { useT } from "../i18n";
import {
    LinkSimple,
    DownloadSimple,
    Spinner,
    YoutubeLogo,
    TiktokLogo,
    InstagramLogo,
    MusicNotes,
    FilmReel,
    FloppyDisk,
    MagnifyingGlass,
    GlobeHemisphereWest,
    X,
    CheckCircle,
    Warning,
    ListPlus,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const PLATFORMS = [
    { id: "youtube", icon: YoutubeLogo, name: "YouTube", match: /youtube\.com|youtu\.be|m\.youtube/i },
    { id: "tiktok", icon: TiktokLogo, name: "TikTok", match: /tiktok\.com|vm\.tiktok/i },
    { id: "instagram", icon: InstagramLogo, name: "Instagram", match: /instagram\.com|instagr\.am/i },
];

function detectPlatform(url) {
    if (!url) return null;
    return PLATFORMS.find((p) => p.match.test(url)) || null;
}

function fmtSpeed(bps) {
    if (!bps) return "—";
    return `${bytesToHuman(bps)}/s`;
}

function fmtEta(s) {
    if (s == null) return "—";
    if (s < 60) return `${Math.round(s)}s`;
    const m = Math.floor(s / 60);
    const r = Math.round(s % 60);
    return `${m}m ${r}s`;
}

function parseUrls(text) {
    return text
        .split(/[\n,\s]+/)
        .map((s) => s.trim())
        .filter((s) => /^https?:\/\//i.test(s));
}

export default function Downloader() {
    const { t } = useT();
    const [urlsText, setUrlsText] = useState("");
    const [format, setFormat] = useState("mp4");
    const [save, setSave] = useState(false);
    const [info, setInfo] = useState(null);
    const [loadingInfo, setLoadingInfo] = useState(false);
    const [jobs, setJobs] = useState([]); // [{job_id, url, ...server fields, clientPct, clientSpeed, grabbed}]
    const pollRef = useRef(null);

    const urls = useMemo(() => parseUrls(urlsText), [urlsText]);
    const firstUrl = urls[0] || "";
    const detected = useMemo(() => detectPlatform(firstUrl), [firstUrl]);

    useEffect(() => {
        if (jobs.length === 0) {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
            return;
        }
        if (pollRef.current) return;
        pollRef.current = setInterval(pollAll, 800);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobs.length]);

    useEffect(() => () => {
        if (pollRef.current) clearInterval(pollRef.current);
    }, []);

    const fetchInfo = async () => {
        if (!firstUrl) return;
        setInfo(null);
        setLoadingInfo(true);
        try {
            const { data } = await api.post("/downloader/info", { url: firstUrl });
            setInfo(data);
        } catch (e) {
            toast.error(fmtErr(e.response?.data?.detail) || t("dl_error_fetch"));
        } finally {
            setLoadingInfo(false);
        }
    };

    const startAll = async () => {
        if (urls.length === 0) return;
        const created = [];
        for (const u of urls) {
            try {
                const { data } = await api.post("/downloader/job", { url: u, format, save });
                created.push({
                    job_id: data.job_id,
                    url: u,
                    status: "queued",
                    progress: 0,
                    downloaded_bytes: 0,
                    total_bytes: 0,
                    speed: 0,
                    eta: null,
                    title: null,
                    filename: null,
                    saved_media_id: null,
                    error: null,
                    format,
                    save,
                    clientPct: null,
                    clientSpeed: 0,
                    grabbed: false,
                });
            } catch (e) {
                toast.error(`${u}: ${fmtErr(e.response?.data?.detail) || e.message}`);
            }
        }
        if (created.length > 0) {
            setJobs((js) => [...js, ...created]);
            toast.success(`${created.length} ${created.length === 1 ? "job pornit" : "joburi pornite"}`);
            // clear textarea but keep info if useful
            setUrlsText("");
            setInfo(null);
        }
    };

    const pollAll = async () => {
        // copy current snapshot
        let current;
        setJobs((js) => {
            current = js;
            return js;
        });
        if (!current) return;
        await Promise.all(
            current
                .filter((j) => j.status !== "done" && j.status !== "error")
                .map(async (j) => {
                    try {
                        const { data } = await api.get(`/downloader/job/${j.job_id}`);
                        setJobs((js) =>
                            js.map((x) => {
                                if (x.job_id !== j.job_id) return x;
                                const merged = { ...x, ...data };
                                if (data.status === "done" && !data.saved_media_id && !x.grabbed) {
                                    grabFile(x.job_id, data.filename || x.filename, x.format);
                                    merged.grabbed = true;
                                }
                                return merged;
                            })
                        );
                    } catch (e) {
                        if (e.response?.status === 404) {
                            // job already cleaned by server
                        }
                    }
                })
        );
    };

    const grabFile = async (jobId, filename, fmtKey) => {
        try {
            const token = getToken();
            const res = await fetch(`${API}/downloader/job/${jobId}/file`, {
                headers: { Authorization: `Bearer ${token}` },
                credentials: "include",
            });
            if (!res.ok) {
                let msg = `HTTP ${res.status}`;
                try { msg = (await res.clone().text()) || msg; } catch {}
                throw new Error(msg);
            }
            const total = parseInt(res.headers.get("content-length") || "0", 10);
            const reader = res.body.getReader();
            const chunks = [];
            let received = 0;
            let lastT = Date.now();
            let lastBytes = 0;
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
                received += value.length;
                const now = Date.now();
                const dt = now - lastT;
                if (dt >= 300) {
                    const sp = ((received - lastBytes) / dt) * 1000;
                    setJobs((js) => js.map((x) => (x.job_id === jobId ? { ...x, clientSpeed: sp } : x)));
                    lastBytes = received;
                    lastT = now;
                }
                if (total > 0) {
                    const pct = Math.min(100, (received / total) * 100);
                    setJobs((js) => js.map((x) => (x.job_id === jobId ? { ...x, clientPct: pct, total_bytes: total } : x)));
                }
            }
            const blob = new Blob(chunks, { type: res.headers.get("content-type") || "application/octet-stream" });
            const cd = res.headers.get("content-disposition") || "";
            const m = cd.match(/filename="([^"]+)"/);
            const fname = m ? m[1] : filename || `download.${fmtKey}`;
            const objUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objUrl; a.download = fname;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(objUrl);
            setJobs((js) => js.map((x) => (x.job_id === jobId ? { ...x, clientPct: 100, clientSpeed: 0 } : x)));
        } catch (e) {
            setJobs((js) => js.map((x) => (x.job_id === jobId ? { ...x, status: "error", error: e.message } : x)));
        }
    };

    const cancelJob = async (jobId) => {
        try { await api.delete(`/downloader/job/${jobId}`); } catch {}
        setJobs((js) => js.filter((x) => x.job_id !== jobId));
    };

    const closeJob = (jobId) => {
        setJobs((js) => js.filter((x) => x.job_id !== jobId));
    };

    const closeAllDone = () => {
        setJobs((js) =>
            js.filter((x) => !(x.status === "done" && (x.saved_media_id || (x.clientPct != null && x.clientPct >= 100))))
        );
    };

    const placeholder = `https://www.youtube.com/watch?v=…
https://www.tiktok.com/@user/video/…
https://www.instagram.com/p/…`;

    const activeCount = jobs.filter((j) => j.status !== "done" && j.status !== "error").length;
    const doneCount = jobs.filter((j) => j.status === "done").length;

    return (
        <div>
            <div className="mb-6">
                <div className="label-mono mb-2">{t("dl_pre")}</div>
                <h1 className="heading-tight fluid-h1">{t("dl_h1")}</h1>
                <p className="fluid-body mt-3 max-w-xl" style={{ color: "var(--text-muted)" }}>
                    {t("dl_desc")}
                </p>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-6 max-w-md">
                {PLATFORMS.map((p) => (
                    <Platform key={p.id} icon={p.icon} name={p.name} active={detected?.id === p.id} />
                ))}
            </div>

            <div className="border p-4 sm:p-6 space-y-5" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="label-mono">{t("dl_url_label")}</label>
                        <span className="label-mono" style={{ color: urls.length ? "var(--text)" : "var(--text-dim)" }} data-testid="urls-count">
                            {urls.length} {urls.length === 1 ? "link" : "linkuri"}
                        </span>
                    </div>
                    <div className="border" style={{ borderColor: "var(--border-strong)", background: "var(--bg)" }}>
                        <textarea
                            data-testid="input-urls"
                            value={urlsText}
                            onChange={(e) => setUrlsText(e.target.value)}
                            placeholder={placeholder}
                            rows={4}
                            className="w-full p-3 bg-transparent outline-none text-sm font-mono resize-y"
                            style={{ color: "var(--text)", minHeight: "8rem" }}
                        />
                        <div className="border-t flex" style={{ borderColor: "var(--border)" }}>
                            <div className="flex items-center px-3 gap-2 flex-1" style={{ color: "var(--text-muted)" }}>
                                <ListPlus size={14} />
                                <span className="label-mono">unul pe linie sau separate prin spațiu</span>
                            </div>
                            <button
                                type="button"
                                onClick={fetchInfo}
                                disabled={!firstUrl || loadingInfo}
                                data-testid="btn-fetch-info"
                                className="btn-press h-10 px-3 flex items-center gap-2 border-l disabled:opacity-50"
                                style={{ borderColor: "var(--border)", color: "var(--text)" }}
                            >
                                {loadingInfo ? <Spinner size={14} className="animate-spin" /> : <MagnifyingGlass size={14} />}
                                <span className="hidden sm:inline text-xs">{t("dl_preview")}</span>
                            </button>
                        </div>
                    </div>
                    {firstUrl && (
                        <div className="mt-2 flex items-center gap-2 text-xs label-mono" data-testid="detected-platform">
                            <GlobeHemisphereWest size={12} />
                            <span>{t("dl_detected")}:</span>
                            <span style={{ color: detected ? "var(--text)" : "var(--warning)" }}>
                                {detected ? detected.name : "—"}
                            </span>
                        </div>
                    )}
                </div>

                {info && (
                    <div data-testid="video-info" className="flex gap-4 border p-3" style={{ borderColor: "var(--border)" }}>
                        {info.thumbnail && <img src={info.thumbnail} alt="" className="w-32 h-20 object-cover" />}
                        <div className="min-w-0 flex-1">
                            <div className="text-sm truncate" style={{ color: "var(--text)" }}>{info.title}</div>
                            <div className="label-mono mt-1">{info.uploader} · {info.extractor}</div>
                            {info.duration && (
                                <div className="text-xs mt-1 font-mono" style={{ color: "var(--text-muted)" }}>
                                    {Math.floor(info.duration / 60)}:{String(info.duration % 60).padStart(2, "0")}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div>
                    <label className="label-mono mb-2 block">{t("dl_format")}</label>
                    <div className="grid grid-cols-2 gap-2">
                        <FormatChoice active={format === "mp4"} onClick={() => setFormat("mp4")} icon={FilmReel} title="MP4" subtitle={t("dl_mp4_sub")} testid="format-mp4" />
                        <FormatChoice active={format === "mp3"} onClick={() => setFormat("mp3")} icon={MusicNotes} title="MP3" subtitle={t("dl_mp3_sub")} testid="format-mp3" />
                    </div>
                </div>

                {format === "mp4" && (
                    <label className="flex items-center gap-2 cursor-pointer text-sm" style={{ color: "var(--text-muted)" }}>
                        <input
                            type="checkbox"
                            checked={save}
                            onChange={(e) => setSave(e.target.checked)}
                            data-testid="chk-save-gallery"
                            className="accent-white w-4 h-4"
                        />
                        <FloppyDisk size={14} />
                        <span>{t("dl_save")}</span>
                    </label>
                )}

                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <button
                        onClick={startAll}
                        disabled={urls.length === 0}
                        data-testid="btn-download"
                        className="btn-press flex-1 h-12 flex items-center justify-center gap-2 disabled:opacity-50"
                        style={{ background: "var(--text)", color: "var(--bg)" }}
                    >
                        <DownloadSimple size={18} />
                        <span className="text-sm font-medium">
                            {t("dl_btn")}{urls.length > 1 ? ` (${urls.length})` : ""}
                        </span>
                    </button>
                </div>

                <div className="text-xs label-mono pt-2" style={{ color: "var(--text-dim)" }}>
                    {t("dl_footer")}
                </div>
            </div>

            {jobs.length > 0 && (
                <JobsList
                    jobs={jobs}
                    activeCount={activeCount}
                    doneCount={doneCount}
                    onCancel={cancelJob}
                    onClose={closeJob}
                    onCloseAllDone={closeAllDone}
                    t={t}
                />
            )}
        </div>
    );
}

function Platform({ icon: Icon, name, active }) {
    return (
        <div
            className="border p-3 flex items-center gap-2 transition-colors"
            style={{
                borderColor: active ? "var(--text)" : "var(--border)",
                background: active ? "var(--surface-2)" : "var(--surface)",
                color: active ? "var(--text)" : "var(--text-muted)",
            }}
            data-testid={`platform-${name.toLowerCase()}`}
        >
            <Icon size={20} />
            <span className="text-xs font-mono">{name}</span>
        </div>
    );
}

function FormatChoice({ active, onClick, icon: Icon, title, subtitle, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className="btn-press text-left p-4 border"
            style={{
                borderColor: active ? "var(--text)" : "var(--border)",
                background: active ? "var(--surface-2)" : "transparent",
                color: "var(--text)",
            }}
        >
            <div className="flex items-center gap-2 mb-1">
                <Icon size={18} />
                <span className="heading-tight text-lg">{title}</span>
            </div>
            <div className="label-mono">{subtitle}</div>
        </button>
    );
}

function JobsList({ jobs, activeCount, doneCount, onCancel, onClose, onCloseAllDone, t }) {
    return (
        <div
            className="fixed left-0 right-0 z-40 px-3 sm:px-6 pointer-events-none"
            style={{ bottom: "calc(env(safe-area-inset-bottom) + 4.5rem)" }}
            data-testid="dl-progress"
        >
            <div
                className="pointer-events-auto max-w-3xl mx-auto border"
                style={{
                    background: "var(--surface)",
                    borderColor: "var(--border-strong)",
                    boxShadow: "0 -20px 40px rgba(0,0,0,0.55)",
                }}
            >
                <div className="flex items-center justify-between gap-2 px-4 py-2 border-b" style={{ borderColor: "var(--border)" }}>
                    <div className="label-mono flex items-center gap-2">
                        <DownloadSimple size={14} />
                        <span>
                            {activeCount > 0 ? `${activeCount} ${activeCount === 1 ? "activă" : "active"}` : t("dl_progress_done")}
                            {doneCount > 0 && ` · ${doneCount} ${doneCount === 1 ? "gata" : "gata"}`}
                        </span>
                    </div>
                    {doneCount > 0 && (
                        <button
                            onClick={onCloseAllDone}
                            data-testid="btn-clear-done"
                            className="btn-press h-8 px-3 text-xs"
                            style={{ color: "var(--text-muted)" }}
                        >
                            curăță finisate
                        </button>
                    )}
                </div>
                <div className="max-h-[40vh] overflow-y-auto divide-y" style={{ borderColor: "var(--border)" }}>
                    {jobs.map((j) => (
                        <JobRow key={j.job_id} job={j} onCancel={() => onCancel(j.job_id)} onClose={() => onClose(j.job_id)} t={t} />
                    ))}
                </div>
            </div>
        </div>
    );
}

function JobRow({ job, onCancel, onClose, t }) {
    const isError = job.status === "error";
    const isSaved = job.status === "done" && job.saved_media_id;
    const isClientPhase = job.status === "done" && job.clientPct != null && !job.saved_media_id;
    const isServerPhase = job.status === "queued" || job.status === "downloading" || job.status === "post_processing";
    const clientDone = isClientPhase && job.clientPct >= 100;

    const pct = isClientPhase ? job.clientPct : Math.min(99, job.progress || 0);
    const speed = isClientPhase ? job.clientSpeed : job.speed;
    const total = job.total_bytes || 0;
    const downloaded = isClientPhase ? Math.round((job.clientPct / 100) * total) : job.downloaded_bytes;

    const label = isError
        ? t("dl_progress_error")
        : isSaved
        ? t("dl_progress_done")
        : isClientPhase
        ? "→ telefon"
        : job.status === "queued"
        ? t("dl_progress_queued")
        : job.status === "post_processing"
        ? t("dl_progress_post")
        : t("dl_progress_downloading");

    const barColor = isError ? "var(--danger)" : isSaved || clientDone ? "var(--success)" : "var(--text)";

    return (
        <div className="px-4 py-3" style={{ borderColor: "var(--border)" }} data-testid={`job-row-${job.job_id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 label-mono mb-1">
                        {isError ? (
                            <Warning size={12} style={{ color: "var(--danger)" }} />
                        ) : isSaved || clientDone ? (
                            <CheckCircle size={12} style={{ color: "var(--success)" }} />
                        ) : (
                            <Spinner size={12} className="animate-spin" />
                        )}
                        <span>{label}</span>
                    </div>
                    <div className="text-xs sm:text-sm truncate" style={{ color: "var(--text)" }} data-testid={`job-title-${job.job_id}`}>
                        {job.title || job.filename || job.url}
                    </div>
                </div>
                <div className="shrink-0">
                    {isServerPhase && (
                        <button
                            onClick={onCancel}
                            data-testid={`btn-cancel-${job.job_id}`}
                            className="btn-press h-7 w-7 flex items-center justify-center border"
                            style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}
                        >
                            <X size={12} />
                        </button>
                    )}
                    {(isError || isSaved || clientDone) && (
                        <button
                            onClick={onClose}
                            data-testid={`btn-close-${job.job_id}`}
                            className="btn-press h-7 w-7 flex items-center justify-center"
                            style={{ background: "var(--text)", color: "var(--bg)" }}
                        >
                            <X size={12} />
                        </button>
                    )}
                </div>
            </div>
            <div className="h-1 w-full mb-1.5 overflow-hidden" style={{ background: "var(--surface-2)" }}>
                <div
                    className="h-1 transition-all duration-300"
                    style={{ width: `${pct || 0}%`, background: barColor }}
                    data-testid={`job-bar-${job.job_id}`}
                />
            </div>
            <div className="flex flex-wrap justify-between gap-x-4 gap-y-0.5 text-[11px] font-mono" style={{ color: "var(--text-muted)" }}>
                <span data-testid={`job-pct-${job.job_id}`}>{(pct || 0).toFixed(1)}%</span>
                {!isError && (
                    <>
                        <span>
                            {bytesToHuman(downloaded)}{total > 0 ? ` / ${bytesToHuman(total)}` : ""}
                        </span>
                        <span>{fmtSpeed(speed)}</span>
                        {!isClientPhase && job.eta != null && job.eta > 0 && <span>ETA {fmtEta(job.eta)}</span>}
                    </>
                )}
                {isError && (
                    <span className="truncate max-w-full" style={{ color: "var(--danger)" }}>{job.error}</span>
                )}
            </div>
        </div>
    );
}
